<script setup lang="ts">
import { ref } from "vue";
import { useTranslation } from "i18next-vue";
import { Logger } from "@/logger";
import router from "@/router";
import {
  SaveState,
  saveDrawing,
  discardUnsavedChanges,
} from "@/stores/SaveDrawingState";
import { Facade } from "@/drawing";

/**
 * Sortie de l'outil dessin (« Retour à l'accueil » du menu fichier) : propose
 * d'enregistrer ou d'abandonner les modifications en attente, puis revient à la
 * page précédente. Passer par ici, jamais par un simple lien : la garde du
 * routeur ne sait que proposer d'abandonner.
 */
const { t } = useTranslation();

const isClosing = ref(false);
const closingStatus = ref("");
const showConfirmDialog = ref(false);

function requestExit() {
  if (SaveState.value === "changed") {
    showConfirmDialog.value = true;
  } else {
    void saveAndLeave();
  }
}

async function saveAndLeave() {
  if (isClosing.value) return;
  Logger.info(`CanvasExitDialog.vue : saveAndLeave()`);
  isClosing.value = true;
  showConfirmDialog.value = false;

  // Only save if there are actual unsaved changes
  if (
    Facade.isInitialized &&
    Facade.file.isOpen() &&
    SaveState.value !== "saved"
  ) {
    closingStatus.value = t("canvas:header.exit.saving");
    await saveDrawing(false);
  }

  router.back();
}

function discardAndLeave(): void {
  showConfirmDialog.value = false;
  isClosing.value = true;
  // Mark as saved (skip the save in onBeforeUnmount) + purge l'oplog pending.
  discardUnsavedChanges();
  router.back();
}

defineExpose({ requestExit });
</script>

<template>
  <span
    v-loading.fullscreen="isClosing"
    :element-loading-text="closingStatus"
    class="d-none"
  />

  <!-- Confirmation dialog for unsaved changes -->
  <el-dialog
    v-model="showConfirmDialog"
    :title="t('canvas:unsavedChanges.title')"
    :close-on-click-modal="false"
    append-to-body
  >
    <p>{{ t("canvas:unsavedChanges.message") }}</p>
    <template #footer>
      <div class="d-flex flex-wrap gap-2 justify-content-end">
        <el-button @click="showConfirmDialog = false">
          {{ t("cancel") }}
        </el-button>
        <el-button type="danger" @click="discardAndLeave">
          {{ t("canvas:unsavedChanges.discard") }}
        </el-button>
        <el-button type="primary" @click="saveAndLeave">
          {{ t("canvas:unsavedChanges.saveAndLeave") }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>
